/**
 * PartitionCard Component
 * Card for configuring one partition/model in the project creation wizard.
 */

import React from 'react';
import {
  Box, Card, CardContent, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Typography, Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { PartitionFormData, SystemScope, AnalysisCode } from '@/types/project';
import ScopeSelector from '@/features/scope/ScopeSelector';
import { isAnalysisCodeSupported } from '@/app/participants/participantCodes';
import {
  MODEL_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  availableForRole,
  restrictScopesForRole,
  roleCountsForCoverage,
  storedRole,
  type ModelRole,
} from '@/types/scope';
import { analysisCodeChoices } from '@/app/participants/analysisCodeChoices';
import { partitionLabel } from '@/features/project-picker/NewProjectWizard/types';
import { MODEL_NAME_MAX } from '@/domain/namePolicy';

interface PartitionCardProps {
  partition: PartitionFormData;
  /** 목록 내 순번(0-기반) — 이름 미입력 시 기본 표시명 'Model {N}'에 사용 */
  index: number;
  availableScopes: SystemScope[];
  onChange: (updated: PartitionFormData) => void;
  onDelete: () => void;
  canDelete: boolean;
}

// 코드 목록의 정본은 analysisCodeRegistry — 손 목록 사본 금지(전수 사냥 B4)

const PartitionCard: React.FC<PartitionCardProps> = ({
  partition,
  index,
  availableScopes,
  onChange,
  onDelete,
  canDelete,
}) => {
  const role: ModelRole = partition.role ?? 'system';

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: 'divider',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1.25,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 0 }}>
            {partitionLabel(partition, index)}
          </Typography>

          <Tooltip title={canDelete ? 'Delete partition' : 'At least one partition is required'}>
            <span>
              <IconButton
                onClick={onDelete}
                disabled={!canDelete}
                size="small"
                aria-label="Delete partition"
                sx={{
                  width: 30,
                  height: 30,
                  color: canDelete ? 'text.secondary' : 'text.disabled',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  '&:hover': {
                    color: 'error.main',
                    borderColor: 'error.light',
                    bgcolor: 'error.lighter',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            mb: 1.25,
          }}
        >
          <TextField
            label="Name"
            value={partition.name}
            onChange={(e) => onChange({ ...partition, name: e.target.value })}
            inputProps={{ maxLength: MODEL_NAME_MAX }}
            size="small"
            required
            sx={{ minWidth: 0 }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              alignItems: 'start',
              gap: 1.25,
              minWidth: 0,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <InputLabel>Analysis Code</InputLabel>
              <Select
                value={partition.analysisCode}
                label="Analysis Code"
                onChange={(e) =>
                  onChange({ ...partition, analysisCode: e.target.value as AnalysisCode })
                }
              >
                {analysisCodeChoices().map((code) => (
                  <MenuItem key={code} value={code}>
                    {isAnalysisCodeSupported(code) ? code : `${code} (지원 예정)`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={ROLE_DESCRIPTIONS[role]} placement="top">
              <FormControl size="small" sx={{ minWidth: 132 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={role}
                  label="Role"
                  data-testid="partition-role"
                  onChange={(e) => {
                    const next = e.target.value as ModelRole;
                    // 역할이 바뀌면 scope 도 그 역할 규칙으로 — 제어기에서 고른 프로젝트 밖 작용 계통이
                    // 계통 모델로 돌아올 때 숨겨진 채 남지 않게(위저드는 자동 채움 없이 사용자가 고른다)
                    onChange({
                      ...partition,
                      role: storedRole(next),
                      scope: restrictScopesForRole(partition.scope, next, availableScopes),
                    });
                  }}
                >
                  {MODEL_ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <TextField
            label="Description"
            value={partition.description || ''}
            onChange={(e) => onChange({ ...partition, description: e.target.value })}
            size="small"
            placeholder="Optional description"
            sx={{ minWidth: 0 }}
          />
        </Box>

        <Box
          sx={{
            pt: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              {roleCountsForCoverage(role) ? 'Model Scope (담당 계통)' : 'Model Scope (작용 계통 — 선택, 커버리지 제외)'}
            </Typography>
            <ScopeSelector
              variant="compact"
              value={partition.scope}
              available={availableForRole(role, availableScopes)}
              onChange={(next: SystemScope[]) => onChange({ ...partition, scope: next })}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PartitionCard;
